import React, { useState } from "react";
import {
  TextField,
  Button,
  Box,
  Typography,
  Grid,
  Alert,
  Snackbar,
} from "@mui/material";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import HeaderNavBar from "../../../header/nav";
import DynamicBreadcrumb from "../../../header/breadcrumb";
import { Image, Modal } from "react-bootstrap";
import Stack from "@mui/material/Stack";
import { useNavigate } from "react-router-dom";
import categoryAPI from "../../../../services/category";

const CategoryForm = () => {
  const [categoryName, setCategoryName] = useState("");
  const [parentCategory, setParentCategory] = useState("");
  const [thumbnail, setThumbnail] = useState(null);
  const [banner, setBanner] = useState(null);
  const [content, setContent] = useState("");
  const [categoryError, setCategoryError] = useState(false);
  const [parentCategoryError, setParentCategoryError] = useState(false);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(false);
  const [thumnailUrl, setThumnailUrl] = useState(null);
  const [bannerUrl, setBannerUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setOpen(false);
  };

  const handleThumnailUpload = (event) => {
    const file = event.target.files[0];

    if (!file) {
      setError("No file selected!");
      setOpen(true);
      return;
    }

    const fileSizeLimit = 1 * 1024 * 1024; // 1MB in bytes
    if (file.size > fileSizeLimit) {
      setError("File size exceeds 1MB. Please upload a smaller image.");
      setOpen(true);
      return;
    }

    const reader = new FileReader();
    reader.readAsDataURL(file); // Read the file as a Base64 URL

    reader.onloadend = () => {
      const image = new window.Image();
      image.src = reader.result;

      image.onload = () => {
        const requiredWidth = 150;
        const requiredHeight = 150;

        if (image.width !== requiredWidth || image.height !== requiredHeight) {
          setError(
            `Image dimensions should be ${requiredWidth}px x ${requiredHeight}px.`
          );
          setOpen(true);
          return;
        }

        // Store the valid image URL and thumbnail
        setThumnailUrl(reader.result);
        setThumbnail(reader.result);
      };

      image.onerror = () => {
        setError("Invalid image file. Please upload a valid image.");
        setOpen(true);
      };
    };

    reader.onerror = () => {
      setError("Error reading the file. Please try again.");
      setOpen(true);
    };
  };

  const handleBannerUpload = (event) => {
    const file = event.target.files[0];

    // Check if the file exists and is an image file
    if (!file) {
      setError("No file selected!");
      setOpen(true);
      return;
    }

    // Check for file size (limit to 1MB)
    const fileSizeLimit = 1 * 1024 * 1024; // 1MB in bytes
    if (file.size > fileSizeLimit) {
      setError("File size exceeds 1MB. Please upload a smaller image.");
      setOpen(true);
      return;
    }

    const reader = new FileReader();
    reader.readAsDataURL(file); // Read the file as a Base64 URL

    reader.onloadend = () => {
      const image = new window.Image(); // Use window.Image to avoid conflicts
      image.src = reader.result;

      image.onload = () => {
        const requiredWidth = 1080;
        const requiredHeight = 150;

        // Check if the image dimensions are correct
        if (image.width !== requiredWidth || image.height !== requiredHeight) {
          setError(
            `Image dimensions should be ${requiredWidth}px x ${requiredHeight}px.`
          );
          setOpen(true);
          return;
        }

        // Store the valid image URL and banner
        setBannerUrl(reader.result);
        setBanner(reader.result);
      };

      image.onerror = () => {
        setError("Invalid image file. Please upload a valid image.");
        setOpen(true);
      };
    };

    reader.onerror = () => {
      setError("Error reading the file. Please try again.");
      setOpen(true);
    };
  };

  const formatFormData = (formData) => {
    const formattedArray = [];

    if (formData.thumbnail) {
      formattedArray.push({
        key: "file",
        description: "",
        type: "file",
        enabled: true,
        value: [formData.thumbnail],
      });
    } else {
      formattedArray.push({
        key: "file",
        description: "",
        type: "file",
        enabled: true,
        value: [],
      });
    }

    formattedArray.push(
      {
        key: "name",
        value: formData.categoryName || "",
        description: "",
        type: "default",
        enabled: true,
      },
      {
        key: "sodexoType",
        value: formData.parentCategory || "",
        description: "",
        type: "default",
        enabled: true,
        value: [formData.banner],
      },
      {
        key: "content",
        value: formData.content || "",
        description: "",
        type: "default",
        enabled: true,
      }
    );

    return formattedArray;
  };
  const handleSave = async (e) => {
    e.preventDefault();
    let hasError = false;

    if (categoryName.trim() === "") {
      setCategoryError(true);
      hasError = true;
    } else {
      setCategoryError(false);
    }

    if (parentCategory.trim() === "") {
      setParentCategoryError(true);
      hasError = true;
    } else {
      setParentCategoryError(false);
    }

    if (!hasError) {
      setLoading(true);
      try {
        let payload = formatFormData({
          categoryName,
          parentCategory,
          content,
          thumbnail,
          banner,
        });
        const response = await categoryAPI.addCategory(payload);
        // console.log("add category response--->", response);
        navigate("/admin/category");
      } catch (error) {
        console.error("Error:", error);
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div>
      <DynamicBreadcrumb />
      {/* Material UI Snackbar and Alert for Error Display */}
      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleClose} severity="error" sx={{ width: "100%" }}>
          {error}
        </Alert>
      </Snackbar>
      <Box
        sx={{
          padding: "20px",
          margin: "10px",
          backgroundColor: "#ffffff", // Box background
          border: "2px solid #dcdcdc",
          borderRadius: "4px",
        }}
      >
        <form onSubmit={handleSave}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={4}>
              <Typography variant="subtitle1">
                Category Name<span className="text-danger">*</span>
              </Typography>
            </Grid>
            <Grid item xs={8}>
              <TextField
                variant="outlined"
                fullWidth
                placeholder="Enter Category Name"
                // required
                value={categoryName}
                onChange={(e) => setCategoryName(e.target.value)}
                error={categoryError}
                helperText={categoryError ? "Category Name is required" : ""}
              />
            </Grid>
          </Grid>

          {/* Parent Category */}
          <Grid container spacing={2} alignItems="center" marginTop={2}>
            <Grid item xs={4}>
              <Typography variant="subtitle1">
                Parent Category<span className="text-danger">*</span>
              </Typography>
            </Grid>
            <Grid item xs={8}>
              <TextField
                variant="outlined"
                fullWidth
                placeholder="Enter Parent Category"
                value={parentCategory}
                onChange={(e) => setParentCategory(e.target.value)}
                error={parentCategoryError}
                helperText={
                  parentCategoryError ? "Parent Category is required" : ""
                }
              />
            </Grid>
          </Grid>

          <Grid container spacing={2} alignItems="center" marginTop={2}>
            <Grid item xs={4}>
              <Typography variant="subtitle1">
                Category Thumbnail <small>Provide thumbnail image</small>{" "}
              </Typography>
            </Grid>
            <Grid item xs={8}>
              <div className="border" style={{ width: "150px" }}>
                {thumnailUrl && (
                  <Image src={thumnailUrl} width={150} height={150} />
                )}
                {!thumnailUrl && (
                  <Image src={"/images/noimage.png"} width={150} height={150} />
                )}
              </div>
              <Stack direction="row" alignItems="center" spacing={2}>
                <label htmlFor="upload-thumnail-image">
                  <Button
                    variant="contained"
                    component="span"
                    style={{ width: "150px", fontSize: "10px" }}
                  >
                    Upload Thumbnail
                  </Button>
                  <input
                    id="upload-thumnail-image"
                    hidden
                    accept="image/*"
                    type="file"
                    onChange={handleThumnailUpload}
                  />
                </label>
              </Stack>
            </Grid>
          </Grid>

          <Grid container spacing={2} alignItems="center" marginTop={2}>
            <Grid item xs={4}>
              <Typography variant="subtitle1">
                Banner Image{" "}
                <small>Provide banner image in horizontal size</small>{" "}
              </Typography>
            </Grid>
            <Grid item xs={8}>
              <div className="border">
                {bannerUrl && (
                  <Image
                    src={bannerUrl}
                    height={150}
                    style={{ width: "100%" }}
                  />
                )}
                {!bannerUrl && (
                  <Image
                    src={"/images/noimage.png"}
                    height={150}
                    style={{ width: "100%" }}
                  />
                )}
              </div>
              <Stack direction="row" alignItems="center" spacing={2}>
                <label htmlFor="upload-banner-image">
                  <Button
                    variant="contained"
                    component="span"
                    style={{ fontSize: "10px" }}
                  >
                    Upload Banner
                  </Button>
                  <input
                    id="upload-banner-image"
                    hidden
                    accept="image/*"
                    type="file"
                    onChange={handleBannerUpload}
                  />
                </label>
              </Stack>
            </Grid>
          </Grid>

          <Grid container spacing={2} alignItems="center" marginTop={2}>
            <Grid item xs={4}>
              <Typography variant="subtitle1">Content</Typography>
            </Grid>
            <Grid item xs={8}>
              <ReactQuill theme="snow" value={content} onChange={setContent} />
            </Grid>
          </Grid>

          <Box marginTop={4}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              disabled={loading}
            >
              Save
            </Button>
          </Box>
        </form>
      </Box>
    </div>
  );
};

export default CategoryForm;
